import { Color, Spacing, Typography } from '@persona-id/design-tokens';
import { EditIcon, XIcon } from '@persona-id/design-tokens/icons';
import { useCallback, useRef } from 'react';
import SignaturePad from 'react-signature-pad-wrapper';
import { WrappedFieldProps } from 'redux-form';
import Markdown from 'shared/components/Markdown';
import styled from 'styled-components';

import Spacer from 'design-system/components/Spacer';
import ButtonBase from 'inquiry/components/ButtonBase';
import Dialog from 'inquiry/components/Dialog';
import FormError from 'inquiry/components/FormError';
import ThemedParagraph from 'inquiry/components/ThemedParagraph';
import ThemedTitle from 'inquiry/components/ThemedTitle';
import { containerStyles, labelStyles } from 'inquiry/mixins/form';
import useToggle from 'lib/hooks/useToggle';
import { colorCss, sizeCss, StyleValueJustify } from 'lib/inquiryStyles';
import { InputESignatureComponent, TextComponentStyles } from 'lib/interfaces/ui-components';

const Container = styled('div')`
  ${containerStyles}
  ${labelStyles}
`;

const SignaturePadWrapper = styled.div<{ styles: InputESignatureComponent['styles'] }>(
  ({ styles }) => ({
    position: 'relative',
    height: '120px',
    borderStyle: 'solid',
    borderColor: colorCss(styles?.['border-color'].base.base),
    borderRadius: sizeCss(styles?.['border-radius'].base.base),
    borderTopWidth: sizeCss(styles?.['border-width'].base.base?.top),
    borderBottomWidth: sizeCss(styles?.['border-width'].base.base?.bottom),
    borderLeftWidth: sizeCss(styles?.['border-width'].base.base?.left),
    borderRightWidth: sizeCss(styles?.['border-width'].base.base?.right),
    '&:hover': {
      cursor: 'pointer',
    },
  }),
);

const SignaturePadOverlay = styled.hr<{ styles: InputESignatureComponent['styles'] }>(
  ({ styles }) => ({
    position: 'absolute',
    left: '10%',
    right: '10%',
    bottom: Spacing[5],
    margin: 'auto',
    borderStyle: 'solid',
    borderColor: colorCss(styles?.['border-color'].base.base),
    borderBottomWidth: sizeCss(styles?.['border-width'].base.base?.bottom),
  }),
);

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${Spacing[3]};
`;

const AddSignatureButton = styled.button<{ styles: InputESignatureComponent['styles'] }>`
  ${Typography.TextMedium};
  height: 160px;
  width: 100%;
  background-color: ${({ styles }) =>
    colorCss(styles?.['background-color']['signature-preview'].base)};
  border: 2px solid ${({ styles }) => colorCss(styles?.['border-color'].base.base)};
  border-radius: ${({ styles }) => sizeCss(styles?.['border-radius'].base.base)};
  &:hover {
    cursor: pointer;
  }
`;

const AddSignaturePreview = styled.button<{ styles: InputESignatureComponent['styles'] }>`
  position: relative;
  height: 160px;
  width: 100%;
  background-color: ${({ styles }) =>
    colorCss(styles?.['background-color']['signature-preview'].base)};
  border: 2px solid ${({ styles }) => colorCss(styles?.['border-color'].base.base)};
  border-radius: ${({ styles }) => sizeCss(styles?.['border-radius'].base.base)};
  &:hover {
    cursor: pointer;
  }
`;

const PreviewImage = styled.img`
  height: 100%;
  width: 100%;
  max-height: 128px; /* Tied to parent's 160px height; determined empirically */
  object-fit: contain;
`;

const EditSignatureIcon = styled(EditIcon)`
  position: absolute;
  top: ${Spacing[4]};
  right: ${Spacing[4]};
`;

const DialogHeaderWrapper = styled.div`
  display: flex;
  justify-content: space-between;
`;

const DialogCloseButton = styled.button<{ styles: InputESignatureComponent['styles'] }>`
  width: 24px;
  background-color: transparent;
  color: ${({ styles }) => colorCss(styles?.['text-color'].label.base)};
  &:hover {
    cursor: pointer;
  }
`;

interface ThemedFormESignatureProps {
  id: string;
  disabled?: boolean;
  label?: string;
  placeholder?: string;
  dialogTitle?: string;
  dialogText?: string;
  clearButtonText?: string;
  saveButtonText?: string;
  styles?: InputESignatureComponent['styles'];
}

const ThemedFormESignature = (props: WrappedFieldProps & ThemedFormESignatureProps) => {
  const {
    input: { onChange, value },
    meta: { error, submitFailed },
    id,
    label,
    placeholder,
    dialogTitle,
    dialogText,
    clearButtonText,
    saveButtonText,
    styles,
    disabled: _disabled, // TODO
  } = props;

  const hasError = !!(error && submitFailed);

  const [showSignatureDialog, { enable: openSignatureDialog, disable: closeSignatureDialog }] =
    useToggle(false);

  const signaturePadRef = useRef<SignaturePad>(null);

  const clearSignature = useCallback(() => {
    signaturePadRef.current?.instance?.clear();
  }, []);

  const saveSignature = useCallback(() => {
    if (signaturePadRef.current?.instance?.isEmpty()) {
      onChange(null);
    } else {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      const signatureData = signaturePadRef.current.toDataURL();
      // NOTE: the Input::Field type currently requires submitting the dataUrl
      // without the leading `data:image/png;base64,` to be processed correctly
      onChange(signatureData != null ? signatureData.split(',')[1] : null);
    }
    closeSignatureDialog();
  }, [onChange, closeSignatureDialog]);

  const primaryButtonStyles = styles?.['button-primary-style']?.['save-signature-button']['base'];
  const secondaryButtonStyles =
    styles?.['button-secondary-style']?.['clear-signature-button']['base'];
  const dialogTitleStyles = styles?.['title-style']?.['dialog-title']['base'];
  const dialogTextStyles = styles?.['text-style']?.['dialog-text']['base'];
  const placeholderStyles: TextComponentStyles = {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    margin: { base: { base: null } },
    justify: { base: { base: StyleValueJustify.Start } },
    'font-family': { base: { base: styles?.['font-family'].placeholder.base } },
    'font-size': { base: { base: styles?.['font-size'].placeholder.base } },
    'font-weight': { base: { base: styles?.['font-weight'].placeholder.base } },
    'letter-spacing': { base: { base: styles?.['letter-spacing'].placeholder.base } },
    'line-height': { base: { base: styles?.['line-height'].placeholder.base } },
    'text-color': { base: { base: styles?.['text-color'].placeholder.base } },
    // FIXME: add highlight color if needed
    'text-color-highlight': { base: { base: styles?.['text-color'].placeholder.base } },
  };

  return (
    <>
      <Container>
        {label && (
          <Markdown<'label'> wrapper='label' wrapperProps={{ htmlFor: `${id}-open-dialog__btn` }}>
            {label}
          </Markdown>
        )}

        {value ? (
          <AddSignaturePreview
            type='button'
            id={id}
            onClick={openSignatureDialog}
            styles={styles}
            aria-errormessage={`${id}--error`}
            aria-describedby={`${id}--error`}
          >
            <EditSignatureIcon size={16} color={Color.CoreSlate800} />
            <PreviewImage src={prependDataUrlMetadata(value)} alt='Current signature preview' />
          </AddSignaturePreview>
        ) : (
          <AddSignatureButton
            type='button'
            id={id}
            onClick={openSignatureDialog}
            styles={styles}
            aria-errormessage={`${id}--error`}
            aria-describedby={`${id}--error`}
          >
            <ThemedParagraph styles={placeholderStyles}>{placeholder}</ThemedParagraph>
          </AddSignatureButton>
        )}

        <FormError id={`${id}--error`} hasError={hasError} errorMessage={error} />
      </Container>

      <Dialog
        isOpen={showSignatureDialog}
        onClose={closeSignatureDialog}
        data-test={`${id}-dialog`}
        aria-labelledby={`${id}-dialog__title`}
        aria-describedby={`${id}-dialog__description`}
      >
        <DialogHeaderWrapper role='presentation'>
          <ThemedTitle id={`${id}-dialog__title`} styles={dialogTitleStyles}>
            {dialogTitle}
          </ThemedTitle>
          <DialogCloseButton
            type='button'
            id={`${id}-dialog-close__btn`}
            onClick={closeSignatureDialog}
            styles={styles}
          >
            <XIcon size={24} aria-hidden />
          </DialogCloseButton>
        </DialogHeaderWrapper>
        <ThemedParagraph id={`${id}-dialog__description`} styles={dialogTextStyles}>
          {dialogText}
        </ThemedParagraph>
        <Spacer height={4} />
        <SignaturePadWrapper role='presentation' styles={styles}>
          <SignaturePad ref={signaturePadRef} height={120} />
          <SignaturePadOverlay role='presentation' styles={styles} />
        </SignaturePadWrapper>
        <Spacer height={4} />
        <ButtonWrapper role='presentation'>
          <ButtonBase
            type='button'
            id={`${id}-dialog-save__btn`}
            styles={secondaryButtonStyles}
            onClick={clearSignature}
          >
            {clearButtonText}
          </ButtonBase>
          <ButtonBase
            type='button'
            id={`${id}-dialog-clear__btn`}
            styles={primaryButtonStyles}
            onClick={saveSignature}
          >
            {saveButtonText}
          </ButtonBase>
        </ButtonWrapper>
      </Dialog>
    </>
  );
};

export default ThemedFormESignature;

const prependDataUrlMetadata = (data: string): string => `data:image/png;base64,${data}`;
